import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useGetActiveAnnouncementsQuery } from '@/store/api/endpoints/client/announcementsApi';
import { getAnnouncementTargetForPath } from '@/components/features/announcements/announcement-meta';
import {
  ACTIVE_ANNOUNCEMENTS_QUERY_PARAMS,
  dismissIssueMaintenanceBanner,
  getIssueMaintenanceAnnouncementIds,
  pickIssueMaintenanceBannerAnnouncement,
  pruneDismissedIssueMaintenanceBannerIds,
  readDismissedIssueMaintenanceBannerIds,
} from '@/modules/announcements/constants';

/**
 * Global issue/maintenance top banner. Dismissals persist in localStorage.
 * Picker skips dismissed ids so the next active
 * issue/maintenance can surface (e.g. dismiss issue → show existing maintenance).
 *
 * No polling: fetches once on mount/full page reload, then re-fetches on
 * route changes so navigating between pages picks up newly active announcements.
 */
export function useIssueMaintenanceBanner() {
  const { pathname } = useLocation();
  const pageContext = getAnnouncementTargetForPath(pathname);

  const { data, isSuccess, refetch } = useGetActiveAnnouncementsQuery(
    ACTIVE_ANNOUNCEMENTS_QUERY_PARAMS,
  );

  const previousPathnameRef = useRef(pathname);
  useEffect(() => {
    const routeChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;
    if (routeChanged) refetch();
  }, [pathname, refetch]);

  const [dismissedIds, setDismissedIds] = useState(() =>
    readDismissedIssueMaintenanceBannerIds(),
  );

  const activeBannerIds = useMemo(
    () => getIssueMaintenanceAnnouncementIds(data?.items),
    [data?.items],
  );

  useEffect(() => {
    if (!isSuccess) return;
    pruneDismissedIssueMaintenanceBannerIds(activeBannerIds);
    setDismissedIds(readDismissedIssueMaintenanceBannerIds());
  }, [activeBannerIds, isSuccess]);

  const announcement = useMemo(
    () => pickIssueMaintenanceBannerAnnouncement(data?.items, dismissedIds, pageContext),
    [data?.items, dismissedIds, pageContext],
  );

  const dismiss = useCallback(() => {
    if (!announcement) return;
    dismissIssueMaintenanceBanner(announcement.id);
    setDismissedIds(readDismissedIssueMaintenanceBannerIds());
  }, [announcement]);

  const isVisible = announcement !== null;

  return {
    announcement,
    isVisible,
    dismiss,
  };
}
